/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env node */

// Záměrně jako CommonsJS modul, aby šlo využít jednotně v CJS i ESM prostředí.

// TODO #488: Nesmí zde být žádné importy, protože se tento souboru bohužel
// tranzitivně načítá z playwright.config.js z e2e CI jobu, kde neděláme
// yarn install.
// const AssertionError = require('assertion-error');

// https://mercury.com/blog/creating-an-emptyobject-type-in-typescript

// eslint-disable-next-line no-unused-vars
const emptySymbol = Symbol('EmptyObject type');
/** @typedef {{ [emptySymbol]?: never }} EmptyObject */

/**
 * TypeScript type guard implementovaný v JS pomocí JSDoc pro ověření,
 * že daná string hodnota patří do hodnot JSDoc enum like objektu (nebo pole).
 *
 * Hodí se zejména při parsování/validaci enumů se specifickým typem pomocí
 * unionu string literálů, tj. např. `@enum {'development' | 'production'}`
 * místo neurčitého `@enum {string}`.
 * @see https://goulet.dev/posts/type-guard-in-jsdoc/
 * @see https://jsdoc.app/tags-enum
 * @template {string} T
 * @param {{ [k: string]: T } | ArrayLike<T>} enumLikeOrArray
 * @param {string} v
 * @returns {v is T}
 */
function enumHasStringValue(enumLikeOrArray, v) {
  const values = /** @type {string[]} */ (Object.values(enumLikeOrArray));
  return values.includes(v);
}
exports.enumHasStringValue = enumHasStringValue;

/**
 * @template T
 * @param {T} constructor
 * @param {any} value
 * @returns {value is T}
 */
function isPrototypeOf(constructor, value) {
  return Object.prototype.isPrototypeOf.call(constructor, value);
}
exports.isPrototypeOf = isPrototypeOf;

/**
 * Identity function. Coerces string/number/boolean literals to literal types.
 * @see https://www.typescriptlang.org/docs/handbook/2/everyday-types.html#literal-types
 * @see https://github.com/microsoft/TypeScript/issues/30445#issuecomment-671042498
 * @template {string | number | boolean} T
 * @param {T} v
 * @returns {T}
 * @readonly
 */
function literal(v) {
  return v;
}
exports.literal = literal;

/**
 * Identity function. Coerces array with string/number/boolean values to tuple type.
 * @template {readonly (string | number | boolean)[]} T
 * @param {T} v
 * @returns {T}
 * @readonly
 */
function tuple(...v) {
  return v;
}
exports.tuple = tuple;

// TODO: Používá se zejména pro Vue data, aby se mohl odvodit typ pro reaktivní
// a nereaktivní data, ale je to poměrně obecné. Chtělo by to refaktorovat
// do funkce s lepším názvem (umístěním), sémantikou a dokumentací.
/**
 * @template {Record<string | number | symbol, any>} T
 * @template {Record<string | number | symbol, any>} Mixin
 * @param {T} v
 * @param {Mixin} mixin
 * @returns {T & Mixin} Vrátí hodnotu {@link v} s typem rozšířeným o {@link mixin}
 * (hodnota se ignoruje).
 */
function mixinType(v, mixin) {
  return /** @type {T & Mixin} */ (v);
}
exports.mixinType = mixinType;

// TODO: Zde navrhují lepší pojmenová assertExhaustive: https://www.fullstory.com/blog/discriminated-unions-and-exhaustiveness-checking-in-typescript/
/**
 * Union Exhaustiveness checking.
 * @see https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html#union-exhaustiveness-checking
 * @example
   function execute(commandEnum: CommandEnum): Promise<void> {
     switch (commandEnum) {
       case CommandEnum.POWER_OFF:
         return this.powerManager.shutdown();
       case CommandEnum.REBOOT:
         return this.powerManager.reboot();
       case CommandEnum.REPORT:
         return this.report();
       default:
         never(commandEnum); // Argument of type 'string' is not assignable to parameter of type 'never'. (parameter) commandEnum: "POWER_ON"
         return Promise.reject(
           new RangeError(`Unknown command "${commandEnum}".`)
         );
    }
   }
 * @param {never} x
 */
function never(x) {}
exports.never = never;

// TODO: Typ chyby, text, atd.
/**
 * @param {*} x
 * @returns {() => TypeError}
 */
function neverDefaultErrorFactory(x) {
  return () => new TypeError(`Unexpected: ${x}`);
}

/**
 * Union Exhaustiveness checking.
 * @see https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html#union-exhaustiveness-checking
 * @example
   function execute(commandEnum: CommandEnum): string {
     switch (commandEnum) {
       case CommandEnum.POWER_OFF:
         return 'shutdown';
       case CommandEnum.REBOOT:
         return 'reboot';
       case CommandEnum.REPORT:
         return 'report';
       default:
         neverThrow(
           commandEnum, // Argument of type 'string' is not assignable to parameter of type 'never'. (parameter) commandEnum: "POWER_ON"
           new RangeError(`Unknown command "${commandEnum}".`));
     }
   }
   @param {never} x
 * @param {() => Error} [errorFactory]
 * @returns {never}
 */
function neverThrow(x, errorFactory = neverDefaultErrorFactory(x)) {
  throw errorFactory();
}
exports.neverThrow = neverThrow;

/**
 * @see https://matiashernandez.dev/blog/post/typescript-how-to-create-a-non-empty-array-type
 * @template T
 * @typedef {[T, ...T[]]} NonEmptyArray
 */

/**
 * @template T
 * @typedef {readonly [T, ...T[]]} ReadonlyNonEmptyArray
 */

/**
 * @see https://matiashernandez.dev/blog/post/typescript-how-to-create-a-non-empty-array-type
 * @template T
 * @param {readonly T[]} array
 * @returns {array is NonEmptyArray<T>}
 */
function isNonEmptyArray(array) {
  return array.length > 0;
}
exports.isNonEmptyArray = isNonEmptyArray;

/**
 * Vrátí true, pokud subset je podmnožinou superset.
 * Kontrolují se klíče i hodnoty mapy.
 * @template K, V
 * @param {ReadonlyMap<K, V>} subset
 * @param {ReadonlyMap<K, V>} superset
 * @returns {boolean}
 */
function isMapSubset(subset, superset) {
  // Prázdná mapa je vždy podmnožinou jakékoli jiné mapy.
  if (subset.size === 0) {
    return true;
  }
  if (subset.size > superset.size) {
    return false;
  }

  for (const [key, val] of subset) {
    if (superset.get(key) !== val) {
      return false;
    }
  }
  return true;
}

exports.isMapSubset = isMapSubset;

/**
 * Sloučí více polí do jednoho Setu. Pořadí prvků se
 * nezachovává a duplikáty jsou odstraněny.
 * @template T
 * @param {T[][]} arrays
 * @returns {Set<T>}
 */
function arraysToSet(...arrays) {
  const merged = new Set();
  arrays.flat().forEach((item) => merged.add(item));
  return merged;
}

exports.arraysToSet = arraysToSet;

/**
 * Rozdělí pole na dvě části podle predikátu. Na indexu 0 jsou prvky,
 * pro které predikát vrací true, na indexu 1 jsou ostatní prvky.
 * @template T
 * @param {T[]} arr
 * @param {(item: T) => boolean} predicate
 * @returns {[T[], T[]]}
 */
function arrayPartition(arr, predicate) {
  return arr.reduce((acc, item) => {
    acc[predicate(item) ? 0 : 1].push(item);
    return acc;
  }, /** @type {[T[], T[]]} */ ([[], []]));
}

exports.arrayPartition = arrayPartition;

/**
 * @see {@link isNonEmptyArray}
 * @template T
 * @param {readonly T[]} array
 * @returns {asserts array is NonEmptyArray<T>}
 * @throws {AssertionError}
 */
function assertIsNonEmptyArray(array) {
  if (!isNonEmptyArray(array)) {
    // TODO #488: Nelze zde mít závislosti.
    // throw new AssertionError('array is empty');
    const error = new Error('array is empty');
    error.name = 'AssertionError';
    throw error;
  }
}
exports.assertIsNonEmptyArray = assertIsNonEmptyArray;

/**
 * @see {@link assertIsNonEmptyArray}
 * @template T
 * @param {readonly T[]} array
 * @returns {NonEmptyArray<T>}
 * @throws {AssertionError}
 */
function ensureNonEmptyArray(array) {
  assertIsNonEmptyArray(array);
  return array;
}
exports.ensureNonEmptyArray = ensureNonEmptyArray;

/**
 * Allows creating a union type by combining primitive types and literal types
 * without sacrificing auto-completion in IDEs for the literal type part
 * of the union.
 *
 * Currently, when a union type of a primitive type is combined with literal
 * types, TypeScript loses all information about the combined literals.
 * Thus, when such type is used in an IDE with autocompletion, no suggestions
 * are made for the declared literals.
 *
 * This type is a workaround for [Microsoft/TypeScript#29729](https://github.com/Microsoft/TypeScript/issues/29729).
 * It will be removed as soon as it's not needed anymore.
 * @see https://github.com/sindresorhus/type-fest/blob/main/source/literal-union.d.ts
 * @template LiteralType
 * @template {Primitive} BaseType
 * @typedef {LiteralType | (BaseType & Record<never, never>)} LiteralUnion
 */

/**
 * Matches any [primitive value](https://developer.mozilla.org/en-US/docs/Glossary/Primitive).
 * @see https://github.com/sindresorhus/type-fest/blob/main/source/primitive.d.ts
 * @typedef {never
 *   | null
 *   | undefined
 *   | string
 *   | number
 *   | boolean
 *   | symbol
 *   | bigint
 * } Primitive
 */

/**
 * @template T
 * @template U
 * @typedef {{ [P in keyof T]: Exclude<T[P], U>; }} MappedExclude
 */

/**
 * Něco jako {@link Required}, ale zachová `undefined`.
 *
 * Pozor, že chování Partial ovlivňuje i volba `strictNullChecks`, kdy
 * `Partial<{ foo: string; }>` může vrátit `{ foo?: string | undefined }` místo
 * `{ foo?: string }`, kde pak ale volání NonPartial vede na zcela jiné výsledky
 * `{ foo: string | undefined }` a `{ foo: string }`, které se liší od vstupního
 * typu!
 *
 * Tím pádem není vždy NonPartial reverzním typem k Partial a měl by se používat
 * jen ve speciálních případech.
 * @see https://www.typescriptlang.org/docs/handbook/2/basic-types.html#strictnullchecks
 * @see https://github.com/microsoft/TypeScript/issues/31025#issuecomment-870011852
 * @template T
 * @typedef {{ [P in keyof Required<T>]: T[P]; }} NonPartial
 */

// TODO #555: Zatím myslím nikde nepoužito - ponechat?

/**
 * From `T` remove a set of properties by value matching `ValueType`.
 * @see https://github.com/piotrwitek/utility-types#omitbyvaluet-valuetype
 * @template T
 * @template ValueType
 * @typedef {Pick<T, { [Key in keyof T]-?: T[Key] extends ValueType ? never : Key }[keyof T]>} OmitByValue
 */

/**
 * From `T` pick a set of properties by value matching `ValueType`.
 * @see https://github.com/piotrwitek/utility-types#pickbyvaluet-valuetype
 * @template T
 * @template ValueType
 * @typedef {Pick<T, { [Key in keyof T]-?: T[Key] extends ValueType ? Key : never }[keyof T]>} PickByValue
 */

/**
 * From `T` pick a set of properties by value matching exact `ValueType`.
 * @see https://github.com/piotrwitek/utility-types#pickbyvalueexactt-valuetype
 * @template T
 * @template ValueType
 * @typedef {Pick<T, { [Key in keyof T]-?: [ValueType] extends [T[Key]] ? [T[Key]] extends [ValueType] ? Key : never : never }[keyof T]>} PickByValueExact
 */

/**
 * @see https://stackoverflow.com/questions/51954558/how-can-i-get-a-union-of-just-the-known-property-keys-from-a-type-that-has-a-str
 * @template T
 * @typedef {{ [ K in keyof T as string extends K ? never : number extends K ? never : K ] : T[K] }} RemoveIndex
 */

/**
 * @see https://stackoverflow.com/questions/51954558/how-can-i-get-a-union-of-just-the-known-property-keys-from-a-type-that-has-a-str
 * @template T
 * @typedef {keyof RemoveIndex<T>} KnownKeys
 */

/**
 * @template {readonly any[]} T
 * @typedef {T extends [head: any, ...tail: infer U] ? U : never} Tail
 */

/**
 * @see https://github.com/microsoft/TypeScript/issues/37642
 * @template {Record<string, string>} T
 * @typedef {T[keyof T]} ValueOf
 */

// TODO: Případně nasadit trochu lepší typování Object.entries/fromEntries
// pokud bude potřeba.
// - https://github.com/microsoft/TypeScript/issues/35101

// TODO #555: Function overloading.
// - https://stackoverflow.com/questions/49523358/document-overloaded-function-in-jsdoc
// - https://stackoverflow.com/questions/72814243/jsdoc-js-overload-functions-methods/73371142
// - TS 5 prý má podporu overloadingu pomocí JSDoc @overload, ale to pro funkce ani metody nefunguje:
//   https://devblogs.microsoft.com/typescript/announcing-typescript-5-0/#overload-support-in-jsdoc

/**
 * @typedef {Object} ObjectId
 * @property {any} id
 */

/**
 * @template {ObjectId} T
 * @typedef {Map<T['id'], T>} IdMap
 */

/**
 * @template {ObjectId} T
 * @typedef {ToReadonlyMap<IdMap<T>>} ReadonlyIdMap
 */

/**
 * @template {ReadonlyMap<any, any>} T
 * @typedef {T extends Map<infer K, infer V> ? ReadonlyMap<K, V> : T} ToReadonlyMap
 */
